import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Bot,
  Loader2,
  MessageSquarePlus,
  Send,
  Square,
  Trash2,
  Volume2,
} from "lucide-react";

import api from "../api/api";
import useAssistant from "../components/assistant/useAssistant";
import SuggestionCards from "../components/assistant/SuggestionCards";
import AssistantAttachment from "../components/assistant/AssistantAttachment";
import AssistantVoice from "../components/assistant/AssistantVoice";
import { mediaCopy } from "../components/assistant/assistantMedia";
import { countingErrorMessage } from "../components/assistant/countingPresentation";
import styles from "../components/assistant/Assistant.module.css";

export default function AiAssistant() {
  const { t } = useTranslation();
  const {
    conversations, activeUid, messages,
    loadingThread, sending, error, errorCode,
    openConversation, startNew, send, remove,
  } = useAssistant();

  const [draft, setDraft] = useState("");
  const [image, setImage] = useState(null);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceIds, setVoiceIds] = useState([]);
  const [voiceEpoch, setVoiceEpoch] = useState(0);
  const [speakingId, setSpeakingId] = useState(null);
  const [ttsLoadingId, setTtsLoadingId] = useState(null);

  const endRef = useRef(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const stopSpeaking = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (audioUrlRef.current) { URL.revokeObjectURL(audioUrlRef.current); audioUrlRef.current = null; }
    setSpeakingId(null);
  };
  useEffect(() => stopSpeaking, []);

  const currentLanguage = () =>
    (i18n.language || localStorage.getItem("local") || "en").split("-")[0].toLowerCase();

  const handleSpeak = async (msg) => {
    const id = msg.messageId;
    if (speakingId === id) { stopSpeaking(); return; }
    stopSpeaking();
    try {
      setTtsLoadingId(id);
      const res = await api.post(
        "/chatbot/speech",
        { text: msg.content, language: currentLanguage() },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      audioUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = stopSpeaking;
      audio.onerror = stopSpeaking;
      setSpeakingId(id);
      await audio.play();
    } catch (e) {
      stopSpeaking();
    } finally {
      setTtsLoadingId(null);
    }
  };

  const submit = async (textOverride) => {
    const text = (textOverride ?? draft).trim();
    if ((!text && !image) || sending || voiceBusy) return;
    setDraft("");
    const file = image;
    setImage(null);
    const transcriptionIds = voiceIds; setVoiceIds([]);
    try {
      await send({ text, image: file, ...(transcriptionIds.length ? { transcriptionIds } : {}) });
    } catch (e) {
      setDraft(text); setImage(file); setVoiceIds(transcriptionIds);
    }
  };

  const showWelcome = !messages.length && !loadingThread;

  return (
    <div className={`px-3 py-3 ${styles.pageShell}`} dir={i18n.dir ? i18n.dir() : "ltr"}>
      <div className={styles.page}>
        {/* conversations */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <button type="button" className={styles.newButton} onClick={() => { setVoiceEpoch((n) => n + 1); setVoiceIds([]); startNew(); }}>
              <MessageSquarePlus size={16} />
              {t("assistant_new_conversation")}
            </button>
          </div>
          <div className={styles.convList}>
            {conversations.length === 0 && (
              <div className={styles.emptyList}>{t("assistant_no_conversations")}</div>
            )}
            {conversations.map((c) => (
              <div
                key={c.uid}
                className={`${styles.convItem} ${c.uid === activeUid ? styles.convItemActive : ""}`}
                onClick={() => openConversation(c.uid)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && openConversation(c.uid)}
              >
                <span className={styles.convTitle}>
                  {c.title || t("assistant_default_conversation")}
                </span>
                <button
                  type="button"
                  className={styles.convAction}
                  title={t("assistant_delete")}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(t("assistant_delete_confirm"))) remove(c.uid);
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* chat */}
        <section className={styles.chat}>
          <div className={styles.thread}>
            {showWelcome && (
              <div className={styles.welcome}>
                <Bot size={40} color="#26a6b7" />
                <div className={styles.welcomeTitle}>{t("assistant_empty_title")}</div>
                <div className={styles.welcomeBody}>{t("assistant_empty_body")}</div>
              </div>
            )}

            {messages.map((m) => {
              const isUser = m.sender === "User";
              const trace = m.toolTrace ? safeParse(m.toolTrace) : null;
              return (
                <div
                  key={m.messageId}
                  className={`${styles.row} ${isUser ? styles.rowUser : styles.rowBot}`}
                >
                  <div className={`${styles.bubble} ${isUser ? styles.bubbleUser : styles.bubbleBot}`}>
                    {m.localImage && m.messageType === "file" && (
                      <video src={m.localImage} controls playsInline preload="metadata" className={styles.messageImage} />
                    )}
                    {m.localImage && m.messageType !== "file" && (
                      <img src={m.localImage} alt="" className={styles.messageImage} />
                    )}
                    {!m.localImage && m.mediaUrl && (
                      <img
                        src={`${process.env.REACT_APP_BASE_URL}${m.mediaUrl}`}
                        alt=""
                        className={styles.messageImage}
                      />
                    )}
                    {!m.localImage && !m.mediaUrl && m.messageType === "file" && (
                      <p className={styles.mediaNotice}>{mediaCopy(i18n.language).expired}</p>
                    )}
                    {isUser ? (
                      <div>{m.content}</div>
                    ) : (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || ""}</ReactMarkdown>
                    )}

                    {!isUser && (
                      <>
                        <div className={styles.actions}>
                          <button
                            type="button"
                            className={`${styles.iconButton} ${speakingId === m.messageId ? styles.iconButtonActive : ""}`}
                            onClick={() => handleSpeak(m)}
                            disabled={ttsLoadingId === m.messageId}
                            title={t("listen_message")}
                            aria-label={t("listen_message")}
                          >
                            {ttsLoadingId === m.messageId ? (
                              <Loader2 size={14} className={styles.spin} />
                            ) : speakingId === m.messageId ? (
                              <Square size={14} />
                            ) : (
                              <Volume2 size={14} />
                            )}
                          </button>
                        </div>
                        {trace && trace.length > 0 && (
                          <details className={styles.trace}>
                            <summary>{t("assistant_sources_used")}</summary>
                            {trace.map((s, i) => (
                              <div key={i}>• {friendlyTool(s.name, t)}</div>
                            ))}
                          </details>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {sending && (
              <div className={`${styles.row} ${styles.rowBot}`}>
                <div className={`${styles.bubble} ${styles.bubbleBot}`}>
                  <div className={styles.typing} role="status" aria-label={mediaCopy(i18n.language).processing}>
                    <span className={styles.dot} />
                    <span className={styles.dot} />
                    <span className={styles.dot} />
                  </div>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {error === "send" && (
            <div className={styles.errorBar} role="alert">{countingErrorMessage(errorCode, i18n.language?.split("-")[0]) || t("assistant_error")}</div>
          )}

          <SuggestionCards onPick={(text) => submit(text)} disabled={sending || voiceBusy} />

          <div className={styles.composer}>
            <AssistantAttachment file={image} onChange={setImage} disabled={sending} language={i18n.language} />
            <div className={styles.composerRow}>
              <input
                dir="auto"
                className={styles.input}
                aria-label={mediaCopy(i18n.language).question}
                value={draft}
                placeholder={t("whatDoYouWantToKnow")}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
                }}
              />
              <AssistantVoice language={i18n.language} disabled={sending || ttsLoadingId != null} resetKey={`${activeUid || "new"}:${voiceEpoch}`}
                onBusyChange={(value) => { setVoiceBusy(value); if (value) stopSpeaking(); }} onTranscript={(text, id) => {
                  setDraft((value) => `${value.trim()}${value.trim() ? " " : ""}${text}`);
                  if (id) setVoiceIds((ids) => [...ids, id].slice(-10));
                }} />
              <button
                type="button"
                className={styles.sendButton}
                aria-label={t("send")}
                onClick={() => submit()}
                disabled={sending || voiceBusy || (!draft.trim() && !image)}
              >
                {sending ? <Loader2 size={16} className={styles.spin} /> : <Send size={16} />}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function safeParse(value) {
  try { return JSON.parse(value); } catch (e) { return null; }
}

/** Tool names are internal; show something a grower can read. */
function friendlyTool(name, t) {
  const key = `assistant_tool_${name}`;
  const label = t(key);
  return label === key ? name.replace(/_/g, " ") : label;
}
